import { TopBarRoute } from "components/templates";
import { useNavigate } from "react-router";
import { Card, Icon, Stack, Tag, Title } from "@introist/react-foundation/v2";
import { api } from "services/rpc/RpcProvider";
import { useWorkflows } from "../hooks/useWorkflows";
import { groupBy } from "lodash";
import { IconPickerIcon } from "../../../components/organisms";
import styled from "styled-components";

const MapGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MapCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr 48px 1fr;
  align-items: center;
  gap: 0;
  padding: 0;
  overflow: hidden;
`;

const SourcesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--palette-surface-default, #f0f0f0);
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--palette-background-default, #ffffff);
  cursor: pointer;

  &:hover {
    background: var(--palette-surface-subdued, #f8f8f8);
  }
`;

const ArrowColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  background: var(--palette-surface-subdued, #f8f8f8);
  border-left: 1px solid var(--palette-surface-default, #f0f0f0);
  border-right: 1px solid var(--palette-surface-default, #f0f0f0);
  color: var(--palette-foreground-dimmed, #999);
  font-size: 18px;
`;

const WorkflowColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  cursor: pointer;
  align-self: stretch;

  &:hover {
    background: var(--palette-surface-subdued, #f8f8f8);
  }
`;

export const WorkflowSourcesRoute = () => {
  const navigate = useNavigate();
  const { workflows } = useWorkflows({});
  const { data: sources } = api.workflows.v4.allTriggeringSources.useQuery(undefined);

  const triggersByWorkflow = groupBy(
    (sources?.triggers ?? []).filter(t => t.workflowId != null),
    t => t.workflowId!
  );
  const creatorsByWorkflow = groupBy(sources?.batchCreators ?? [], c => c.workflowId);
  const stepsByWorkflow = groupBy(sources?.workflowSteps ?? [], s => s.targetWorkflowId);

  const connectedWorkflows = (workflows ?? []).filter(
    wf =>
      (triggersByWorkflow[wf.id]?.length ?? 0) > 0 ||
      (creatorsByWorkflow[wf.id]?.length ?? 0) > 0 ||
      (stepsByWorkflow[wf.id]?.length ?? 0) > 0
  );

  return (
    <TopBarRoute
      paths={[
        {
          key: "activation-map",
          title: "Activation map"
        }
      ]}
      medium
    >
      <Stack vertical gap="xLarge">
        <Stack>
          <Title variant="bold">Activation map</Title>
          <Tag colorVariant="surface">{connectedWorkflows.length}</Tag>
        </Stack>
        <MapGrid>
          {connectedWorkflows.map(wf => {
            const triggers = triggersByWorkflow[wf.id] ?? [];
            const creators = creatorsByWorkflow[wf.id] ?? [];
            const steps = stepsByWorkflow[wf.id] ?? [];
            const allSources = [
              ...triggers.map(t => ({ type: "trigger" as const, item: t })),
              ...creators.map(c => ({ type: "creator" as const, item: c })),
              ...steps.map(s => ({ type: "step" as const, item: s }))
            ];

            return (
              <MapCard key={wf.id}>
                <SourcesColumn>
                  {allSources.map(({ type, item }) => {
                    if (type === "trigger") {
                      const trigger = item as (typeof triggers)[0];
                      return (
                        <SourceRow
                          key={trigger.id}
                          onClick={() =>
                            window.open(
                              `/triggers/${trigger.id}`,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        >
                          <Icon name="trigger" />
                          <Title style={{ flex: 1 }}>{trigger.name}</Title>
                          <Tag colorVariant={trigger.active ? "primary" : "surface"}>
                            {trigger.active ? "Active" : "Inactive"}
                          </Tag>
                        </SourceRow>
                      );
                    }
                    if (type === "creator") {
                      const creator = item as (typeof creators)[0];
                      return (
                        <SourceRow
                          key={creator.id}
                          onClick={() =>
                            window.open(`/batches`, "_blank", "noopener,noreferrer")
                          }
                        >
                          <Icon name="featureRoutines" />
                          <Title style={{ flex: 1 }}>{creator.title}</Title>
                          <Tag colorVariant={creator.start ? "primary" : "surface"}>
                            {creator.start ? "Active" : "Inactive"}
                          </Tag>
                        </SourceRow>
                      );
                    }
                    const step = item as (typeof steps)[0];
                    return (
                      <SourceRow
                        key={step.id}
                        onClick={() =>
                          window.open(
                            `/flows/edit/${step.parentWorkflowId}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <Icon name="workflows" />
                        <Stack vertical gap="none" style={{ flex: 1 }}>
                          <Title>{step.stepTitle ?? step.stepType}</Title>
                          <Title style={{ fontSize: "12px", opacity: 0.5 }}>
                            {step.parentWorkflowTitle}
                          </Title>
                        </Stack>
                        <Tag colorVariant={step.active ? "primary" : "surface"}>
                          {step.active ? "Active" : "Inactive"}
                        </Tag>
                      </SourceRow>
                    );
                  })}
                </SourcesColumn>

                <ArrowColumn>→</ArrowColumn>

                <WorkflowColumn onClick={() => navigate(`/flows/edit/${wf.id}`)}>
                  <IconPickerIcon icon="workflows" fallbackIcon="workflows" />
                  <Title variant="bold">{wf.title}</Title>
                </WorkflowColumn>
              </MapCard>
            );
          })}
        </MapGrid>
      </Stack>
    </TopBarRoute>
  );
};
